import { Flex, Text } from '@mantine/core';
import { MantineTable } from '@dealstack/frontend/shared/components/Table/MantineTable/MantineTable';
import { LoanDefinitionType, SecurityType } from '@dealstack/shared/lib/generated';
import { TEST_ELEMENT_IDS } from '@dealstack/shared/tests/testElementIds';
import { InfoBox } from '@dealstack/frontend/shared/components/InfoBox/InfoBox';
import { useHoldingTable } from './useHoldingTable';
import { HoldingOrLoan } from './holdingTable.types';
import { HoldingVestingDetail } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Investors/InvestorDetails/components/HoldingTable/HoldingVestingDetail.tsx';
import { useTheme } from 'styled-components';

interface Props {
	getSecurityTypeTranslation: (type?: SecurityType | LoanDefinitionType) => string;
}

export const HoldingTable = ({ getSecurityTypeTranslation }: Props) => {
	const theme = useTheme();
	const { data, columns, valuationsLoading, investorId, tMipManage, dataOperations } =
		useHoldingTable({
			getSecurityTypeTranslation,
		});

	const getRowCanExpand = (row: { original: HoldingOrLoan }) => {
		const holding = row.original;
		return (
			'isSubjectToVesting' in holding &&
			!!holding.isSubjectToVesting &&
			'securityId' in holding &&
			!!holding.securityId
		);
	};

	return (
		<div data-testid={TEST_ELEMENT_IDS.MIP_MANAGE_INVESTORS_DETAILS_HOLDING_TABLE}>
			<Text fz="lg" fw={800} mt="2rem" mb="1rem">
				{tMipManage('investors.details.holdings_table.title')}
			</Text>

			{data?.length || valuationsLoading ? (
				<MantineTable
					data={data}
					columns={columns}
					getRowId={(row, index) =>
						'securityId' in row && row.securityId ? row.securityId : `holding-${index}`
					}
					dataOperations={dataOperations}
					manualSorting={false}
					enablePagination={false}
					enableSearchBar={false}
					enableTopToolbar={false}
					enableRowSelection={false}
					enableFilters={false}
					enableHiding={false}
					enableExpanding
					getRowCanExpand={getRowCanExpand}
					isLoading={valuationsLoading}
					mantineTableBodyRowProps={() => ({
						style: {
							height: 'unset',
						},
					})}
					mantineDetailPanelProps={{
						style: {
							borderTop: 'none',
							borderBottom: `1px solid ${theme.v3.colors.gray[4]}`,
						},
					}}
					renderDetailPanel={({ row }) => {
						const holding = row.original;
						const securityId = 'securityId' in holding ? holding.securityId : undefined;
						if (!securityId) {
							return null;
						}
						return (
							<HoldingVestingDetail investorId={investorId} securityId={securityId} />
						);
					}}
				/>
			) : (
				<Flex maw="60rem">
					<InfoBox
						iconVariant="information"
						alignContent="center"
						title={tMipManage('investors.details.alerts.holdings_title')}
						description={tMipManage('investors.details.alerts.holdings_description')}
					/>
				</Flex>
			)}
		</div>
	);
};
